import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, Signature, AlertTriangle, FileText, ExternalLink, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicSignPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  const load = useCallback(async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const r = await axios.get(API + "/public/sign/" + token);
      setState({ loading: false, data: r.data, error: null });
      if (r.data.signer_name) setName(r.data.signer_name);
    } catch (e) {
      setState({ loading: false, data: null, error: e.response?.data?.detail || e.message });
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Nom complet requis"); return; }
    if (!consent) { toast.error("Consentement requis"); return; }
    setSubmitting(true);
    try {
      await axios.post(API + "/public/sign/" + token, { signed_name: name, consent: true });
      toast.success("Signature enregistree");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSubmitting(false);
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-[#14101c]"><Loader2 className="w-10 h-10 animate-spin text-[#f7c935]" /></div>;

  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#14101c] px-4">
      <div className="bg-[#0b0811] border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
        <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-3" />
        <h1 className="text-xl font-black text-white mb-2">Lien invalide</h1>
        <p className="text-sm text-slate-400">{state.error}</p>
      </div>
    </div>
  );

  const d = state.data;

  if (d.status === "signed") return (
    <div className="min-h-screen flex items-center justify-center bg-[#14101c] px-4">
      <div className="bg-[#0b0811] border border-green-500/30 rounded-2xl p-8 max-w-md text-center" data-testid="sign-success">
        <CheckCircle2 className="w-14 h-14 mx-auto text-green-400 mb-3" />
        <h1 className="text-2xl font-black text-white mb-2">Signature enregistree</h1>
        <p className="text-sm text-slate-400">Le {new Date(d.signed_at).toLocaleString("fr-FR")}</p>
        <p className="text-sm text-white font-semibold mt-3">{d.signed_name}</p>
        <p className="text-[11px] text-slate-500 mt-1">Role: {d.role}{d.signed_via === "yousign_eidas" && " · via Yousign eIDAS Avancé"}</p>
        <p className="text-[10px] text-slate-500 mt-4">Vous pouvez fermer cette page.</p>
      </div>
    </div>
  );

  const yousignAvailable = d.yousign?.available && d.yousign?.signature_link;

  return (
    <div className="min-h-screen bg-[#14101c] py-10 px-4" data-testid="public-sign-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto bg-[#f7c935]/20 border border-[#f7c935]/40 rounded-2xl flex items-center justify-center mb-4">
            <Signature className="w-7 h-7 text-[#f7c935]" />
          </div>
          <h1 className="text-3xl font-black text-white">Signature electronique</h1>
          {d.organisme_name && <p className="text-slate-400 text-sm mt-1">{d.organisme_name}</p>}
        </div>

        <div className="bg-[#0b0811] border border-white/10 rounded-2xl p-6 mb-4">
          <div className="flex items-start gap-3 mb-4">
            <FileText className="w-6 h-6 text-[#f7c935] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-white">{d.document.doc_name}</div>
              <div className="text-xs text-slate-400 mt-0.5 capitalize">{d.document.doc_type?.replace(/_/g, " ")}</div>
            </div>
            {d.document.pdf_url && (
              <a href={d.document.pdf_url} target="_blank" rel="noreferrer" className="text-xs text-[#f7c935] hover:underline flex items-center gap-1" data-testid="sign-preview-pdf">
                <ExternalLink className="w-3 h-3" /> Consulter
              </a>
            )}
          </div>
          <div className="bg-white/5 rounded-lg p-3 text-xs text-slate-400">
            <strong className="text-white">Role du signataire :</strong> <span className="capitalize">{d.role?.replace(/_/g, " ")}</span><br />
            <strong className="text-white">Lien valide jusqu'au :</strong> {new Date(d.expires_at).toLocaleDateString("fr-FR")}
          </div>
        </div>

        {/* Iter 234 — Si Yousign disponible : bandeau en avant + fallback option */}
        {yousignAvailable && !showFallback ? (
          <div className="bg-gradient-to-br from-purple-500/10 to-fuchsia-500/10 border border-purple-500/30 rounded-2xl p-6 mb-4" data-testid="yousign-banner">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-purple-300" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-purple-400 font-bold flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Recommandé · eIDAS Avancé
                </div>
                <div className="text-base font-bold text-white mt-1">Signer ma convention en 2 clics</div>
                <div className="text-xs text-slate-300 mt-1">
                  Signature électronique <strong>légalement contraignante</strong> conforme Qualiopi 2025+. Authentification par OTP envoyé sur votre email.
                </div>
              </div>
            </div>

            {/* Iter 234c — QR code pour scan mobile (parcours stagiaire le plus court) */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-3 flex items-center gap-4" data-testid="yousign-qr-section">
              <img
                src={`${API}/yousign/qr/${token}`}
                alt="QR code signature mobile"
                className="w-24 h-24 rounded-lg bg-white p-1.5 flex-shrink-0"
                data-testid="yousign-qr-img"
              />
              <div className="text-xs text-slate-300">
                <div className="font-bold text-purple-300 mb-1 flex items-center gap-1">
                  📱 Sur votre téléphone
                </div>
                <div className="text-[11px]">Scannez ce QR code avec l'appareil photo de votre smartphone pour signer en 30 secondes (l'OTP arrivera sur le même appareil).</div>
              </div>
            </div>

            <Button
              onClick={() => { window.location.href = d.yousign.signature_link; }}
              className="w-full bg-purple-500 hover:bg-purple-400 text-white font-bold h-12"
              data-testid="yousign-sign-cta"
            >
              <ShieldCheck className="w-5 h-5 mr-2" /> Signer maintenant via Yousign
            </Button>
            <button
              onClick={() => setShowFallback(true)}
              className="w-full mt-2 text-xs text-slate-400 hover:text-slate-200"
              data-testid="show-fallback-btn"
            >
              ou utiliser la signature interne (eIDAS simple)
            </button>
          </div>
        ) : (
          <div className="bg-[#0b0811] border border-white/10 rounded-2xl p-6">
            {yousignAvailable && (
              <button onClick={() => setShowFallback(false)} className="text-xs text-purple-400 hover:text-purple-300 mb-3 flex items-center gap-1">
                ← Revenir à Yousign (recommandé)
              </button>
            )}
            <div className="text-[10px] uppercase tracking-wider text-[#f7c935] font-bold mb-3">Votre signature</div>
            <label className="text-xs text-slate-400 block mb-1">Nom complet *</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Prenom Nom" className="bg-[#14101c] border-white/10 text-white text-lg mb-3" data-testid="sign-name" />
            <label className="flex items-start gap-2 text-xs text-slate-300 cursor-pointer mb-5">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="sign-consent" />
              <span>J'atteste avoir pris connaissance du document et accepte de le signer electroniquement. Cette signature a la meme valeur juridique qu'une signature manuscrite.</span>
            </label>
            <Button onClick={submit} disabled={!name.trim() || !consent || submitting} className="w-full bg-[#f7c935] text-[#14101c] font-bold h-12" data-testid="sign-submit">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Signature className="w-5 h-5 mr-2" />}Signer electroniquement
            </Button>
          </div>
        )}

        <p className="text-[10px] text-center text-slate-600 mt-4">
          Blitzz · {yousignAvailable ? "signature qualifiée eIDAS via Yousign · valeur juridique équivalente à une signature manuscrite" : "signature à valeur probante (RGPD + eIDAS simple)"}
        </p>
      </div>
    </div>
  );
}
